import React, { useEffect, useMemo, useState } from 'react';
import { Select, SelectItem, Selection, Input } from '@nextui-org/react';
import { MarketData, Settlement } from 'common/src/utils/types';
import { useTranslation } from '../../hooks/useTranslation';

interface ReceiverFilterProps {
  data: MarketData[] | Settlement[];
  selectedReceiver: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  defaultSelectedKeys?: string[];
  defaultSelectAll?: boolean;
  hideAllOption?: boolean;
  excludeReceiver?: string;
}

const ReceiverFilter: React.FC<ReceiverFilterProps> = ({
  data,
  selectedReceiver,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
  defaultSelectedKeys,
  defaultSelectAll = false,
  hideAllOption = false,
  excludeReceiver,
}) => {
  const { t } = useTranslation('common');
  const finalLabel = label || t('Cliente');
  const [search, setSearch] = useState('');

  const receivers = useMemo(() => {
    const unique = new Set<string>();
    data.forEach((item) => {
      let receiver = '';
      if ('receiver' in item) {
        receiver = item.receiver;
      } else if ('settlement_details' in item) {
        receiver = item.client || 'Sin Cliente';
      }
      if (receiver && receiver !== excludeReceiver) {
        unique.add(receiver);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [data, excludeReceiver]);

  const filteredReceivers = useMemo(() => {
    if (!search) return receivers;
    const lower = search.toLowerCase();
    return receivers.filter((r) => r.toLowerCase().includes(lower));
  }, [receivers, search]);

  useEffect(() => {
    if (
      selectionMode === 'multiple' &&
      defaultSelectAll &&
      receivers.length > 0
    ) {
      onSelectionChange(receivers);
    }
  }, []);

  if (receivers.length === 0) {
    return null;
  }

  const handleSelectionChange = (keys: Selection) => {
    if (selectionMode === 'multiple') {
      const keysArray = Array.from(keys);

      if (keysArray.includes('all')) {
        // If 'all' is selected and there were previous selections, clear all
        if (Array.isArray(selectedReceiver) && selectedReceiver.length > 0) {
          onSelectionChange([]);
        } else {
          // Otherwise, select all receivers
          onSelectionChange(receivers);
        }
      } else {
        // Normal selection behavior for individual items
        onSelectionChange(keysArray as string[]);
      }
    } else {
      // Single selection behavior
      const selectedKeys = Array.from(keys);
      onSelectionChange(selectedKeys as string[]);
    }
  };

  return (
    <Select
      label={finalLabel}
      selectedKeys={
        selectionMode === 'multiple'
          ? Array.isArray(selectedReceiver)
            ? new Set(selectedReceiver)
            : new Set([])
          : new Set([selectedReceiver as string])
      }
      selectionMode={selectionMode}
      defaultSelectedKeys={defaultSelectedKeys && new Set(defaultSelectedKeys)}
      className={className}
      size={size}
      aria-label={`${t('Selecciona cliente')}`}
      renderValue={(items) => {
        if (
          Array.isArray(selectedReceiver) &&
          selectedReceiver.length === receivers.length &&
          receivers.length > 0
        ) {
          return (
            <div className='flex items-center gap-2'>
              {t('Todos los clientes')}
            </div>
          );
        }
        if (
          Array.isArray(selectedReceiver) &&
          selectedReceiver.length === 0
        ) {
          return (
            <div className='flex items-center gap-2 text-default-400'>
              {t('Ninguno')}
            </div>
          );
        }
        if (Array.isArray(selectedReceiver) && selectedReceiver.length > 0) {
          return (
            <div className='flex items-center gap-2'>
              {selectedReceiver.join(', ')}
            </div>
          );
        }
        return items;
      }}
      listboxProps={{
        topContent: (
          <div className='sticky top-0 z-10 bg-content1 px-1 pt-1 pb-1'>
            <Input
              size='sm'
              placeholder={t('Buscar cliente...')}
              value={search}
              onValueChange={setSearch}
              isClearable
              onClear={() => setSearch('')}
            />
          </div>
        ),
      }}
      onSelectionChange={handleSelectionChange}
    >
      {selectionMode === 'single' && !hideAllOption && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          {t('Todos')}
        </SelectItem>
      )}
      {selectionMode === 'multiple' && !hideAllOption && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          <div className='flex justify-between items-center'>
            <span>
              {Array.isArray(selectedReceiver) && selectedReceiver.length > 0
                ? t('Deseleccionar todos')
                : t('Seleccionar todos')}
            </span>
          </div>
        </SelectItem>
      )}
      {filteredReceivers.map((receiver) => (
        <SelectItem key={receiver} value={receiver} textValue={receiver}>
          {receiver}
        </SelectItem>
      ))}
    </Select>
  );
};

export default React.memo(ReceiverFilter);
